import { Button, Grid, Select, TextInput } from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { IconChevronRight } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Device } from '@/types/device'
import { isValidUrl } from '@/utils/validation/url'

const EXAMPLE_URL =
  'http(s)://<aibridge host>:<aibridge port>/api/bridge/graphql'

export type FormValues = {
  deviceId: string
  milestoneUrl: string
}

type MilestoneUrlFormProps = {
  devices: Device[]
  isLoading?: boolean
  onSubmit: (formValues: FormValues) => void
}

export const MilestoneUrlForm = ({
  devices,
  isLoading,
  onSubmit
}: MilestoneUrlFormProps) => {
  const intl = useIntl()

  const form = useForm({
    initialValues: {
      deviceId: devices[0]?.id || '',
      milestoneUrl: ''
    },
    validate: {
      deviceId: isNotEmpty(
        intl.formatMessage({ id: 'cameras.import.milestone.device.required' })
      ),
      milestoneUrl: (value) => {
        return isValidUrl(value)
          ? null
          : intl.formatMessage({
              id: 'cameras.import.milestone.url.invalid'
            })
      }
    }
  })

  return (
    <form noValidate onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <Select
        label={<FormattedMessage id="cameras.fields.device" />}
        data={devices.map((device) => ({
          value: device.id,
          label: device.label
        }))}
        allowDeselect={false}
        nothingFoundMessage={
          <FormattedMessage id="cameras.device.noOnlineDevices" />
        }
        disabled={isLoading}
        mb="xl"
        {...form.getInputProps('deviceId')}
      />

      <TextInput
        type="url"
        label={<FormattedMessage id="cameras.import.milestone.url.label" />}
        description={EXAMPLE_URL}
        disabled={isLoading}
        inputWrapperOrder={['label', 'input', 'description', 'error']}
        inputContainer={(children) => (
          <Grid gutter="xs">
            <Grid.Col span="auto">{children}</Grid.Col>

            <Grid.Col span="content">
              <Button
                miw={160}
                rightSection={<IconChevronRight size={18} />}
                loading={isLoading}
                type="submit"
              >
                <FormattedMessage id="next" />
              </Button>
            </Grid.Col>
          </Grid>
        )}
        autoFocus
        data-autofocus
        {...form.getInputProps('milestoneUrl')}
      />
    </form>
  )
}
